import { useState } from "react";
import { useTranslation } from "react-i18next";
import MarkdownRenderer from "../content/MarkdownRenderer";
import Tabs from "../navigation/Tabs";
import { FORM_COMPACT_CONTROL_CLASS, FORM_LABEL_WITH_SPACING_CLASS } from "../styles/formStyles";

type MarkdownView = "write" | "preview";

/**
 * A Markdown text with a preview of how participants and agents will read it. `labelHidden`
 * where a section title above already names it; `lang` where the text is in a given language;
 * `placeholder` what readers see while it is empty.
 */
export default function MarkdownField({ label, labelHidden = false, lang, placeholder, value, rows, onChange }: { label: string; labelHidden?: boolean; lang?: string; placeholder?: string; value: string; rows: number; onChange: (value: string) => void }) {
  const { t } = useTranslation();
  const [view, setView] = useState<MarkdownView>("write");
  return (
    <Tabs
      label={label}
      options={[
        { key: "write", label: t("authoring.markdown.write") },
        { key: "preview", label: t("authoring.markdown.preview") },
      ]}
      selected={view}
      onSelect={setView}
      header={labelHidden ? null : <span className={FORM_LABEL_WITH_SPACING_CLASS}>{label}</span>}
    >
      {view === "preview" ? (
        <div lang={lang} className="app-surface-muted min-h-24 rounded p-3">
          {/* Empty, it previews what readers see meanwhile, where that is given (WD-1+3 4c, Sol r2 C3). */}
          {value.trim() !== "" ? (
            <MarkdownRenderer content={value} className="app-markdown text-sm" />
          ) : placeholder !== undefined && placeholder.trim() !== "" ? (
            <MarkdownRenderer content={placeholder} className="app-markdown app-text-muted text-sm" />
          ) : (
            <p className="app-text-muted text-sm">{t("authoring.markdown.empty")}</p>
          )}
        </div>
      ) : (
        <textarea aria-label={label} lang={lang} placeholder={placeholder} className={`${FORM_COMPACT_CONTROL_CLASS} font-mono text-xs`} rows={rows} value={value} onChange={(event) => { onChange(event.target.value); }} />
      )}
    </Tabs>
  );
}
